import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../../auth/Auth';
import { getEVoucherAmount, redeemEVoucher } from '../../../auth/_requests';
import { useModal } from '../../hooks/modalContext';
import {
  E_VOUCHER_CODE_LENGTH,
  formatEVoucherCode,
  getEVoucherErrorMessage,
  normalizeEVoucherCode,
} from './eVoucherUtils';

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const EVoucher = () => {
  const { setModal, modalState } = useModal();
  const { auth } = useAuth();
  const token = auth?.data?.token;

  const [code, setCode] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemedAmount, setRedeemedAmount] = useState<number | null>(null);

  useEffect(() => {
    setAmount(null);
    setError('');
    if (code.length !== E_VOUCHER_CODE_LENGTH) return;

    let cancelled = false;
    const validate = async () => {
      setValidating(true);
      try {
        const voucherAmount = await getEVoucherAmount(token, code);
        if (!cancelled) setAmount(voucherAmount);
      } catch (err) {
        if (!cancelled) setError(getEVoucherErrorMessage(err));
      } finally {
        if (!cancelled) setValidating(false);
      }
    };
    void validate();

    return () => {
      cancelled = true;
    };
  }, [code, token]);

  const handleRedeem = async () => {
    if (amount === null) return;
    setRedeeming(true);
    try {
      await redeemEVoucher(token, code);
      setRedeemedAmount(amount);
      modalState?.onEVoucherRedeemed?.();
    } catch (err) {
      setAmount(null);
      setError(getEVoucherErrorMessage(err));
    } finally {
      setRedeeming(false);
    }
  };

  if (redeemedAmount !== null) {
    return (
      <motion.div
        className='relative flex w-[400px] max-w-[92vw] flex-col items-center rounded-[12px] bg-[#FFF] px-10 py-10'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <CheckCircle2 className='text-[#16A34A]' size={40} />
        <span className='mt-3 text-center text-lg font-semibold text-main'>
          You&apos;ve redeemed {formatCurrency(redeemedAmount)} in rent credit
        </span>
        <span className='mb-6 mt-2 text-center text-sm text-[#6B7285]'>
          Your rent credit will be applied first on your next payment.
        </span>
        <button type='button' className='submitBtn w-full' onClick={() => setModal(null)}>
          Done
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      className='relative flex w-[400px] max-w-[92vw] flex-col rounded-[12px] bg-[#FFF] px-10 py-10'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <span className='text-center text-lg font-semibold text-main'>Redeem e-voucher</span>
      <span className='mb-5 mt-2 text-center text-sm text-[#6B7285]'>
        Enter your 16-character e-voucher code to receive rent credit.
      </span>

      <input
        className='w-full rounded-[10px] border border-[#E9EDF9] px-4 py-3 text-center text-lg uppercase tracking-widest text-main outline-none focus:border-[#0055FF]'
        placeholder='XXXX XXXX XXXX XXXX'
        value={formatEVoucherCode(code)}
        onChange={(event) => setCode(normalizeEVoucherCode(event.target.value))}
        maxLength={E_VOUCHER_CODE_LENGTH + 3}
        autoComplete='off'
        autoFocus
        aria-label='E-voucher code'
        aria-invalid={Boolean(error)}
      />

      <div className='mb-5 mt-3 min-h-[20px] text-center text-sm' aria-live='polite'>
        {validating && <span className='text-[#6B7285]'>Checking voucher…</span>}
        {error && <span className='text-[#B70D0D]'>{error}</span>}
        {amount !== null && (
          <span className='font-semibold text-[#16A34A]'>
            Worth {formatCurrency(amount)} in rent credit
          </span>
        )}
      </div>

      <div className='flex w-full justify-between gap-5'>
        <button
          type='button'
          className='cancelBtn w-full'
          disabled={redeeming}
          onClick={() => setModal(null)}
        >
          Cancel
        </button>
        <button
          type='button'
          className='submitBtn w-full'
          disabled={amount === null || validating || redeeming}
          onClick={() => void handleRedeem()}
        >
          {redeeming ? 'Redeeming...' : 'Redeem'}
        </button>
      </div>
    </motion.div>
  );
};

export { EVoucher };
